import React from 'react';
import { Grid, TextField, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Image as ImageIcon, PhotoCamera as CameraIcon } from '@mui/icons-material';
import { partial, times } from 'lodash';
import RubricBlock from '@dyl/blocks/RubricBlock';
import SectionDividers from '@dyl/components/SectionDividers';
import useChangeEvent from '@dyl/hooks/useChangeEvent';
import useTaskField from '@dyl/pages/Tasks/edit/useTaskField';
import RadioGroupDyl from '@dyl/components/RadioGroupDyl';
import RadioDyl from '@dyl/components/RadioDyl';
import TaskRadioImagesBlock from '@dyl/blocks/TaskRadioImagesBlock';
import TaskRadioTextBlock from "@dyl/blocks/TaskRadioTextBlock";
import ImagePicker from "@dyl/components/ImagePicker";
import ChipList from '@dyl/components/ChipList';
import useUrlLevel from '@dyl/hooks/useUrlLevel';
import SimpleTaskField from '@dyl/blocks/SimpleTaskField';

const CHIPLIST_CHARACTER_LIMIT = 750;
const useStyles = makeStyles(theme => ({
    root: {
        maxWidth: theme.breakpoints.values.md,
    },
    partHeader: {
        marginBottom: '16px',
        textAlign: 'center'
    },
}));

export default function OnlineMessagesEditor() {
    const classes = useStyles();
    const [items] = useTaskField('items');
    const { urlLevel } = useUrlLevel();

    return (
        <div className={classes.root}>
            <SectionDividers>
                {times(urlLevel === 'A2' && items.length > 5 ? items.length-1 : items.length, i => {
                    return (
                        <div key={i + 100}>
                            {(i === 0 || i === 4) &&
                                <Typography key={i + 100} variant='h4' className={classes.partHeader}>
                                    {`Part ${i === 0 ? 1 : 2}`}
                                </Typography>
                            }
                            {items[i].phase === "IMAGE"
                                ? <ImageItem key={i} index={i} />
                                : items[i].phase === "TEXT"
                                    ? <TextItem key={i} index={i} />
                                    : <WritingItem key={i} index={i} />
                            }
                        </div>
                    )
                }
                )}
            </SectionDividers>
        </div>
    );
}

function ImageItem({ index }) {
    const isExample = index === 0;
    const useField = partial(useTaskField, 'items', index);

    const [questionStimulus, setQuestionStimulus, qsError] = useField('system_question');
    const onSqChange = useChangeEvent(setQuestionStimulus);

    const [answerStimulus, setAnswerStimulus, answerStimulusError] = useField('user_answer_stimulus_text');

    const onAnswerStimulusChange = useChangeEvent(setAnswerStimulus);

    const [icon, setIcon, iconError] = useField('icon_indicator');
    const onIconChange = useChangeEvent(setIcon);

    return (
        <>
            <Typography variant='h6'>
                {isExample ? 'Example' : `Item ${index}`}
            </Typography>

            {isExample && (<RubricBlock index={index} />)}

            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <TextField
                        label='System Question'
                        value={questionStimulus}
                        onChange={onSqChange}
                        variant='filled'
                        fullWidth
                        helperText={qsError || ' '}
                        error={!!qsError}
                    />
                </Grid>
                <Grid item xs={12} md={6}>
                    <TextField
                        label='Answer Stimulus'
                        value={answerStimulus}
                        onChange={onAnswerStimulusChange}
                        variant='filled'
                        fullWidth
                        helperText={answerStimulusError || ' '}
                        error={!!answerStimulusError}
                    />
                </Grid>
                <Grid item md={12}></Grid>
            </Grid>
            <>
                <RadioGroupDyl
                    label='Selection Icon'
                    helperText={iconError || ' '}
                    error={!!iconError}
                    value={icon}
                    onChange={onIconChange}
                >
                    <RadioDyl value='Camera' label={<CameraIcon />} />
                    <RadioDyl value='Image' label={<ImageIcon />} />
                </RadioGroupDyl>
                <TaskRadioImagesBlock path={['items', index, 'answers']} />
            </>
        </>
    );
}

function TextItem({ index }) {
    const useField = partial(useTaskField, 'items', index);

    const [questionStimulus, setQuestionStimulus, qsError] = useField('system_question');
    const onSqChange = useChangeEvent(setQuestionStimulus);
    return (
        <>
            <Grid container spacing={2}>

                <Typography variant='h6'>
                    {`Item ${index}`}
                </Typography>


                <Grid item xs={12} md={6}>
                    <TextField
                        label='System Question'
                        value={questionStimulus}
                        onChange={onSqChange}
                        variant='filled'
                        fullWidth
                        helperText={qsError || ' '}
                        error={!!qsError}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <StimulusImage itemIndex={index} />
                </Grid>
                <Grid item md={12}></Grid>
            </Grid>
            <TaskRadioTextBlock path={['items', index, 'answers']} />
        </>
    );
}


function WritingItem({ index }) {
    const useField = partial(useTaskField, 'items', index);
    const [questionStimulus, setQuestionStimulus, qsError] = useField('system_question');
    const onSqChange = useChangeEvent(setQuestionStimulus);
    const { urlLevel } = useUrlLevel();
    const writingItemsChipList = [{ label: 'Choice A Correct Answers' }, { label: 'Choice B Correct Answers' }, { label: 'Choice C Correct Answers' }]
    return (
        <>
            <Typography variant='h6'>
                {`Item ${index}`}
            </Typography>


            {index === 4 && (<RubricBlock index={index} />)}

            <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                    <TextField
                        label='System Question'
                        value={questionStimulus}
                        onChange={onSqChange}
                        variant='filled'
                        fullWidth
                        helperText={qsError || ' '}
                        error={!!qsError}
                    />
                </Grid>
                <Grid item md={12}></Grid>
            </Grid>

            <TaskRadioImagesBlock hasCheckbox={false} path={['items', index, 'answers']} />
            {urlLevel !== 'A2' && writingItemsChipList.map((item, indexKey) => <WritingItemChipList itemIndex={indexKey} index={index} label={item.label} key={indexKey} />)}
            {
                urlLevel === 'A2' && (
                    <>
                        <SimpleTaskField
                            required
                            label='Image A keyword(s)'
                            path={['items', index, 'answers', 0, 'key']}
                            helperText='Please type the keywords, delimited by commas.'
                            limit={CHIPLIST_CHARACTER_LIMIT}
                            style={{ marginTop: '24px' }}
                        />
                        <SimpleTaskField
                            required
                            label='Image B keyword(s)'
                            path={['items', index, 'answers', 1, 'key']}
                            helperText='Please type the keywords, delimited by commas.'
                            limit={CHIPLIST_CHARACTER_LIMIT}
                            style={{ marginTop: '24px' }}
                        />
                        <SimpleTaskField
                            required
                            label='Image C keyword(s)'
                            path={['items', index, 'answers', 2, 'key']}
                            helperText='Please type the keywords, delimited by commas.'
                            limit={CHIPLIST_CHARACTER_LIMIT}
                            style={{ marginTop: '24px' }}
                        />

                        <Typography
                            variant='h5'
                            sx={{
                                fontFamily: 'Roboto, Arial, sans-serif',
                                fontWeight: 600,
                                fontStyle: 'normal',
                                fontSize: '20px',
                                lineHeight: '100%',
                                letterSpacing: '0',
                                marginTop: '40px',
                                marginBottom: '24px'
                            }}
                        >
                            {`Training Sentences`}
                        </Typography>

                        <Grid item xs={12}>
                            <SimpleTaskField
                                required
                                label='Image A Training Sentences'
                                path={['items', index, 'answers', 0, 'training_sentences']}
                                helperText='Please type at least 5 training sentences delimited by commas.'
                                multiline
                                rows={3}
                                style={{ marginTop: '24px' }}

                            />
                        </Grid>
                        <Grid item xs={12}>
                            <SimpleTaskField
                                required
                                label='Image B Training Sentences'
                                path={['items', index, 'answers', 1, 'training_sentences']}
                                helperText='Please type at least 5 training sentences delimited by commas.'
                                multiline
                                rows={3}
                                style={{ marginTop: '24px' }}

                            />
                        </Grid>
                        <Grid item xs={12}>
                            <SimpleTaskField
                                required
                                label='Image C Training Sentences'
                                path={['items', index, 'answers', 2, 'training_sentences']}
                                helperText='Please type at least 5 training sentences delimited by commas.'
                                multiline
                                rows={3}
                                style={{ marginTop: '24px' }}

                            />
                        </Grid>
                    </>
                )
            }
        </>
    );
}


function StimulusImage({ itemIndex }) {
    const [imageId, setImageId, imageError] = useTaskField('items', itemIndex, 'image_id');
    return (
        <ImagePicker
            value={imageId ?? ''}
            onChange={setImageId}
            label={'Stimulus Image'}
            helperText={imageError || ''}
            error={!!imageError}
        />
    );
}

function WritingItemChipList({ itemIndex, index, label }) {
    const [answers, setAnswers, answersError] = useTaskField('items', index, 'answers', itemIndex, 'key');
    return (
        <Grid item xs={12} style={{ marginBottom: '24px' }}>
            <ChipList
                value={answers}
                onChange={setAnswers}
                label={label}
                error={!!answersError}
                helperText={
                    answersError
                    || `Answers that will be accepted as correct${index === 0 ? ' (max 1 for example)' : ''}.`}
                required
                max={index === 0 ? 1 : undefined}
                maxLen={CHIPLIST_CHARACTER_LIMIT}
                isOnlineMessages = {true}
            />
        </Grid>
    )
}